// Package rendered book HTML into valid EPUB 3.0 format.
// EPUB is a zip file with specific structure: mimetype, META-INF/container.xml, OEBPS/content.opf, etc.

import JSZip from 'jszip';
import type { ParsedBook, BookChapter } from './book-parser';
import { bookTitle, bookSubtitle } from './book-parser';
import { renderBookCss, renderMarkdownToBookHtml } from './book-renderer';

function escapeXml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function chapterXhtml(title: string, bodyHtml: string, css: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${escapeXml(title)}</title>
  <link rel="stylesheet" href="style.css" />
</head>
<body>
${bodyHtml}
</body>
</html>`;
}

function buildCoverXhtml(book: ParsedBook, coverImageFile?: string): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  const subtitle = bookSubtitle(book.metadata.levelName);

  const imgTag = coverImageFile
    ? `<img src="${coverImageFile}" alt="Cover" style="max-width:100%;height:auto;border-radius:12px;margin-bottom:2rem;" />`
    : '';

  return chapterXhtml(`${title} — Cover`, `
<div class="book-cover">
  ${imgTag}
  <div class="cover-title">${escapeXml(title)}</div>
  <div class="cover-subtitle">${escapeXml(subtitle)}</div>
  <div class="cover-level">CEFR ${escapeXml(book.metadata.level)}</div>
</div>`, '');
}

// Glossika app download buttons — Blitzscale email style, logos inlined as XHTML-valid SVG (offline-safe).
const APP_STORE_BUTTONS_XHTML = `<div class="title-appstore">
    <div class="title-appstore-label">Get the Glossika app</div>
    <a href="https://apps.apple.com/app/id6667095609" style="display:inline-block;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:6px 12px;text-decoration:none;margin-right:6px;"><svg width="14" height="14" viewBox="0 0 384 512" fill="#cbd5e1" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;margin-right:6px;"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg><span style="font-size:12px;color:#cbd5e1;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;vertical-align:middle;">App Store</span></a><a href="https://play.google.com/store/apps/details?id=com.glossika.video" style="display:inline-block;background:#1e293b;border:1px solid #334155;border-radius:8px;padding:6px 12px;text-decoration:none;"><svg width="14" height="14" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;margin-right:6px;"><path fill="#00e2ff" d="M48.6 26.9C42.9 33 39.6 42.5 39.6 54.8v402.4c0 12.3 3.3 21.8 9.3 27.6l1.3 1.2 225.4-225.4v-5.3L50.2 27.6z"/><path fill="#00d95f" d="m350.5 335.3-75.2-75.2v-5.3l75.2-75.2 1.7 1 89 50.6c25.5 14.4 25.5 38.1 0 52.6l-89 50.5z"/><path fill="#ff3a44" d="m352.2 334.3-76.9-76.9L48.6 484.1c8.4 8.9 22.3 10 38 1.1z"/><path fill="#ffc400" d="M352.2 180.5 86.6 26.8c-15.7-9-29.6-7.8-38 1.1l226.7 226.6z"/></svg><span style="font-size:12px;color:#cbd5e1;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;vertical-align:middle;">Google Play</span></a>
  </div>`;

function buildTitlePageXhtml(book: ParsedBook, buyerEmailPlaceholder: string): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  const subtitle = bookSubtitle(book.metadata.levelName);

  return chapterXhtml(`${title} — Title`, `
<div class="book-title-page">
  <h1>${escapeXml(title)}</h1>
  <div class="title-subtitle">${escapeXml(subtitle)} — CEFR ${escapeXml(book.metadata.level)}</div>
  <div class="title-author">by Michael Campbell</div>
  <div class="title-copyright">
    © 2026 Glossika Inc. All rights reserved.<br />
    No part of this publication may be reproduced, distributed, or transmitted
    in any form without prior written permission of the publisher.
  </div>
  ${buyerEmailPlaceholder ? `<div class="title-drm">Licensed to: ${escapeXml(buyerEmailPlaceholder)}</div>` : ''}
  ${APP_STORE_BUTTONS_XHTML}
</div>`, '');
}

function buildTocXhtml(book: ParsedBook): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  let items = '';
  for (const ch of book.chapters) {
    items += `<li class="toc-item"><a href="chapter-${ch.id}.xhtml">${escapeXml(`${ch.number}. ${ch.title}`)}</a></li>\n`;
  }
  return chapterXhtml(`${title} — Contents`, `
<div class="book-toc">
  <h2>Contents</h2>
  <ul class="toc-list">${items}</ul>
</div>`, '');
}

function buildChapterXhtml(ch: BookChapter, chapterImageUrl?: string): string {
  let body = '';
  if (ch.subsections.length > 0) {
    for (const sub of ch.subsections) {
      body += `<div class="book-section">
        <h3 class="book-section-title">${escapeXml(sub.title)}</h3>
        ${renderMarkdownToBookHtml(sub.content)}
      </div>`;
    }
  } else {
    body = renderMarkdownToBookHtml(ch.content);
  }

  const hero = chapterImageUrl
    ? `<img class="book-chapter-image" src="${chapterImageUrl}" alt="${escapeXml(ch.title)}" />`
    : '';

  return chapterXhtml(ch.title, `
<div class="book-chapter" id="${ch.id}">
  <div class="book-chapter-header">
    <div class="book-chapter-number">Chapter ${ch.number}</div>
    <div class="book-chapter-title">${escapeXml(ch.title)}</div>
  </div>
  ${hero}
  ${body}
</div>`, '');
}

function buildTocNcx(book: ParsedBook, bookId: string): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  let points = '';
  let order = 1;

  points += `<navPoint id="cover" playOrder="${order++}"><navLabel><text>Cover</text></navLabel><content src="cover.xhtml" /></navPoint>\n`;
  points += `<navPoint id="title" playOrder="${order++}"><navLabel><text>Title Page</text></navLabel><content src="title.xhtml" /></navPoint>\n`;
  points += `<navPoint id="toc" playOrder="${order++}"><navLabel><text>Contents</text></navLabel><content src="toc.xhtml" /></navPoint>\n`;

  if (book.preamble.trim()) {
    points += `<navPoint id="preamble" playOrder="${order++}"><navLabel><text>Introduction</text></navLabel><content src="chapter-ch-preamble.xhtml" /></navPoint>\n`;
  }

  for (const ch of book.chapters) {
    points += `<navPoint id="${ch.id}" playOrder="${order++}"><navLabel><text>${escapeXml(ch.title)}</text></navLabel><content src="chapter-${ch.id}.xhtml" /></navPoint>\n`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
  <head>
    <meta name="dtb:uid" content="${bookId}" />
    <meta name="dtb:depth" content="1" />
    <meta name="dtb:totalPageCount" content="0" />
    <meta name="dtb:maxPageNumber" content="0" />
  </head>
  <docTitle><text>${escapeXml(title)}</text></docTitle>
  <navMap>${points}</navMap>
</ncx>`;
}

function buildNavXhtml(book: ParsedBook): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  let items = `<li><a href="cover.xhtml">Cover</a></li>\n`;
  items += `<li><a href="title.xhtml">Title Page</a></li>\n`;
  items += `<li><a href="toc.xhtml">Contents</a></li>\n`;

  if (book.preamble.trim()) {
    items += `<li><a href="chapter-ch-preamble.xhtml">Introduction</a></li>\n`;
  }

  for (const ch of book.chapters) {
    items += `<li><a href="chapter-${ch.id}.xhtml">${escapeXml(`${ch.number}. ${ch.title}`)}</a></li>\n`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops">
<head><title>${escapeXml(title)}</title></head>
<body>
<nav epub:type="toc" id="toc">
  <h1>Table of Contents</h1>
  <ol>${items}</ol>
</nav>
</body>
</html>`;
}

function buildContentOpf(book: ParsedBook, bookId: string): string {
  const title = bookTitle(book.metadata.languageName, book.metadata.levelName);
  const subtitle = bookSubtitle(book.metadata.levelName);

  let manifestItems = `
    <item id="style" href="style.css" media-type="text/css" />
    <item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml" />
    <item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav" />
    <item id="cover-page" href="cover.xhtml" media-type="application/xhtml+xml" />
    <item id="title-page" href="title.xhtml" media-type="application/xhtml+xml" />
    <item id="toc-page" href="toc.xhtml" media-type="application/xhtml+xml" />`;

  let spineItems = `
    <itemref idref="cover-page" />
    <itemref idref="title-page" />
    <itemref idref="toc-page" />`;

  if (book.preamble.trim()) {
    manifestItems += `\n    <item id="ch-preamble" href="chapter-ch-preamble.xhtml" media-type="application/xhtml+xml" />`;
    spineItems += `\n    <itemref idref="ch-preamble" />`;
  }

  for (const ch of book.chapters) {
    manifestItems += `\n    <item id="${ch.id}" href="chapter-${ch.id}.xhtml" media-type="application/xhtml+xml" />`;
    spineItems += `\n    <itemref idref="${ch.id}" />`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="BookId" version="3.0">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
    <dc:identifier id="BookId">urn:uuid:${bookId}</dc:identifier>
    <dc:title>${escapeXml(title)} — ${escapeXml(subtitle)}</dc:title>
    <dc:creator>Michael Campbell</dc:creator>
    <dc:publisher>Glossika Inc.</dc:publisher>
    <dc:language>en</dc:language>
    <dc:rights>© 2026 Glossika Inc. All rights reserved.</dc:rights>
    <dc:date>2026</dc:date>
    <dc:subject>Language Learning</dc:subject>
    <dc:subject>CEFR ${escapeXml(book.metadata.level)}</dc:subject>
    <dc:subject>${escapeXml(book.metadata.languageName)}</dc:subject>
    <meta property="dcterms:modified">${new Date().toISOString().replace(/\.\d+Z$/, 'Z')}</meta>
  </metadata>
  <manifest>${manifestItems}
  </manifest>
  <spine toc="ncx">${spineItems}
  </spine>
</package>`;
}

export interface EpubBuildOptions {
  coverImageBlob?: Blob;
  buyerEmail?: string;
  chapterImageUrls?: Record<string, string>;   // keyed by chapter id (e.g. "ch-12"); data URIs
}

export async function buildEpub(book: ParsedBook, options: EpubBuildOptions = {}): Promise<Blob> {
  const zip = new JSZip();
  const bookId = uuid();
  const buyerEmail = options.buyerEmail ?? '';   // no placeholder; the real line is stamped at delivery

  // mimetype must be first file, stored uncompressed
  zip.file('mimetype', 'application/epub+zip', { compression: 'STORE' });

  // META-INF
  zip.file('META-INF/container.xml', `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml" />
  </rootfiles>
</container>`);

  // OEBPS
  const oebps = zip.folder('OEBPS')!;

  // CSS
  oebps.file('style.css', renderBookCss());

  // Content OPF
  oebps.file('content.opf', buildContentOpf(book, bookId));

  // TOC NCX
  oebps.file('toc.ncx', buildTocNcx(book, bookId));

  // Navigation document
  oebps.file('nav.xhtml', buildNavXhtml(book));

  // Cover
  const coverImgFile = options.coverImageBlob ? 'cover.jpg' : undefined;
  if (options.coverImageBlob) {
    oebps.file('cover.jpg', options.coverImageBlob);
  }
  oebps.file('cover.xhtml', buildCoverXhtml(book, coverImgFile));

  // Title page
  oebps.file('title.xhtml', buildTitlePageXhtml(book, buyerEmail));

  // TOC page
  oebps.file('toc.xhtml', buildTocXhtml(book));

  // Preamble chapter
  if (book.preamble.trim()) {
    const preambleBody = `
<div class="book-chapter" id="ch-preamble">
  <div class="book-chapter-header">
    <div class="book-chapter-number">Introduction</div>
    <div class="book-chapter-title">Language Overview</div>
  </div>
  ${renderMarkdownToBookHtml(book.preamble)}
</div>`;
    oebps.file('chapter-ch-preamble.xhtml', chapterXhtml('Introduction', preambleBody, ''));
  }

  // Content chapters
  for (const ch of book.chapters) {
    oebps.file(`chapter-${ch.id}.xhtml`, buildChapterXhtml(ch, options.chapterImageUrls?.[ch.id]));
  }

  return zip.generateAsync({ type: 'blob', mimeType: 'application/epub+zip' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
